import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";

import { AddEvent,  GridDataResult, GridModule } from "@progress/kendo-angular-grid";
import { State } from "@progress/kendo-data-query";
import { forkJoin, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { AuthorizationService } from "@myapp/modules/fwcore/services";

import { AsyncPipe, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { ComboBoxModule } from "@progress/kendo-angular-dropdowns";
import { LabelModule } from "@progress/kendo-angular-label";
import {
    AuthPermIdentifier, DatePreferencePipe, EmptyNumberFormatPipe,
    NullableBooleanClassDirective, RequestUserPermission
} from "../../../../modules/fwcore";

import { ControlResizerOptions, ControlResizerService } from "../../../../../services/framework";
import { TriStateCheckBoxComponent } from "../../../widgets/components/tri-state-check-box/tri-state-check-box.component";
import { LicenseGridEditorComponent } from "../../components/licenses/license-grid-editor/license-grid-editor.component";
import { Company, ICompany, License, RequestLicense } from "../../models";
import { AdminService, LicenseServiceGrid } from "../../services";


@Component({
  selector: 'app-page-license',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [GridModule, NgIf,  LabelModule, ComboBoxModule, FormsModule, TriStateCheckBoxComponent
    , ButtonModule,  NullableBooleanClassDirective,  LicenseGridEditorComponent
    , AsyncPipe, DatePreferencePipe, EmptyNumberFormatPipe],
  templateUrl: './page-license.component.html',
  styleUrls: ['./page-license.component.scss']
})

export class PageLicenseComponent implements OnInit, AfterViewInit, OnDestroy {

  /*-------------------------- Input / Outputs --------------------------------*/



  /*-------------------------- public properties / methods --------------------*/

  /*-------------------------- Internal properties  ---------------------------*/

  protected editDataItem?: License;

  protected destroy$ = new Subject();

  protected heightGrid!: number;

  protected viewInit: boolean = false;

  protected licActive?: boolean | null = true;

  protected view: Observable<GridDataResult>;

  protected isNew?: boolean;

  protected selectedCompanyItem?: Company;

  protected companies: ICompany[] | null = null;

  protected req: RequestLicense = {
    skip: 0
    , take: 15
  };

  protected reqPerm: RequestUserPermission = {

  }

  protected perms?: AuthPermIdentifier[] = [];

  /*-------------------------- State & permissions  --------------------------*/

  protected state: State = {
    skip: 0
    , take: 15
    , sort: []
  }

  protected showAdd: boolean = false;
  protected showEditButton: boolean = false;
  protected showReadOnlyButton: boolean = false;
  protected showOneSysSettings: boolean = false;

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected service: LicenseServiceGrid
    , protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected controlResizer: ControlResizerService
    , protected router: Router) {

    this.view = service;
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {

    this.req.active = this.licActive;

    this.reqPerm.route = this.router.url;
    this.service.query(this.req);

    forkJoin([
      this.serviceAuth.pagePermissionsGet$(this.reqPerm),
      this.adminService.getAllCompanies()
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([permissions, companies]) => {
        this.perms = permissions.data ?? [];

        this.showAdd = this.perms?.some(i => i.code === "license-create") ?? false;
        this.showEditButton = this.perms?.some(i => i.code === "license-edit") ?? false;
        this.showReadOnlyButton = this.perms?.some(i => i.code === "license-detail") ?? false;
        this.showOneSysSettings = this.perms?.some(i => i.code === "license-detail-onesys") ?? false;

        this.companies = companies;

      });

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  protected onSearchClick(): void {

    this.req.companyId = (this.selectedCompanyItem) ? this.selectedCompanyItem.id : null;
    this.req.active = this.licActive;

    this.state.skip = 0;
    this.req.skip = 0;

    this.service.query(this.req);
  }

  protected onTemplatesClick() {
    this.router.navigate(['./admin/role-template']);
  }

  protected onRolesClick(dataItem: License): void {

    this.router.navigate(
      ['/admin/role']
      , { queryParams: { id: dataItem.id } }
    );
  }

  protected onLicenseUserManageClick(dataItem: License): void {

    this.router.navigate(
      ['/admin/user/license']
      , { queryParams: { id: dataItem.id } }
    );
  }

  protected onUserPermissionsClick(dataItem: License) {
    this.router.navigate(['./admin/user/permission'], { queryParams: { id: dataItem.id } });
  }

  // -------------- Grid processing ------------------------------------------------

  protected onStateChange(state: State): void {

    this.state = state;

    this.req.skip = this.state.skip;
    this.req.take = this.state.take;

    this.service.query(this.req);
  }

  protected onAddLicense(): void {

    let lic: License = new License();

    lic.isActive = false;
    lic.enforceEndDate = false;
    lic.enforceUsers = false;

    this.editDataItem = lic;

    this.isNew = true;
  }

  protected onEditLicense(args: AddEvent): void {

    this.editDataItem = args.dataItem;
    this.isNew = false;
  }

  protected onCancelLicense(): void {
    this.editDataItem = undefined;
  }

  protected onSaveLicense(item: License): void {

    if (item.id) {

      let merged = { ...this.editDataItem, ...item };

      this.service.save([merged])
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
    else
      this.service.save([item])
        .pipe(takeUntil(this.destroy$))
        .subscribe();

    this.editDataItem = undefined;
  }

  /*--------------------------- internal functions  --------------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 54;
      options.offsetBottomHeight = 75;

      this.heightGrid = this.controlResizer.calculateHeight(options);
    }
  }

  protected getCompanyName(id: string): string {
    return this.companies!.find(co => co.id == id)!.name ?? '';
  }
}
